import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Modal, IconButton, Slide } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { usePostHog } from 'posthog-js/react';
import { ColorType } from '../../../../assets/colors/colors.g';
import CustomButton from '../../../react-native-material-wrapper/button.component';
import PercentageDiscountIcon from '../../../../assets/icons/percentage-discount.svg';
import CheckCircleIcon from '../../../../assets/icons/check-circle.svg';
import { PromoCodeProps } from './promo-code.interface';
import { PROMO_CODE_ERROR_CODES } from '../../../../services/promocode/promo-code-api.interface';
import { usePromoCodeStyles, ICON_SIZES } from './promo-code.styles';
import { useApplyPromoCodeMutation } from '../../../../services/promocode/promo-code.api';
import {
  normalizeApplyPromoCodeResponse,
  formatDiscountValue,
  PROMO_CODE_ERRORS,
} from './promo-code.utils';

const PromoCode: React.FC<PromoCodeProps> = ({ sessionId, appliedPromoCode }) => {
  const classes = usePromoCodeStyles();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [appliedCode, setAppliedCode] = useState(appliedPromoCode);
  const [isSuccess, setIsSuccess] = useState(false);
  const [applyPromoCodeMutation] = useApplyPromoCodeMutation();

  useEffect(() => {
    setAppliedCode(appliedPromoCode);
  }, [appliedPromoCode]);

  const handleOpen = () => {
    setIsModalOpen(true);
    setError(null);
    setPromoCode('');
    if (appliedCode) {
      setIsSuccess(true);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setPromoCode('');
    setError(null);
    setIsSuccess(false);
  };

  const getErrorMessage = (errorCode: string, code: string): string => {
    switch (errorCode) {
      case PROMO_CODE_ERROR_CODES.INVALID_CODE:
        return `${code} ${t('promo_code_cannot_be_used')}`;
      case PROMO_CODE_ERROR_CODES.NOT_APPLICABLE_TO_CHARGER:
        return t('promo_code_not_applicable');
      case PROMO_CODE_ERROR_CODES.USAGE_LIMIT_REACHED:
        return t('promo_code_usage_limit');
      case PROMO_CODE_ERROR_CODES.ALREADY_APPLIED:
        return t('promo_code_already_applied');
      default:
        return `${code} ${t('promo_code_cannot_be_used')}`;
    }
  };

  const handleApply = async () => {
    // Clear any previous errors when applying
    setError(null);

    const code = promoCode.trim();

    if (!code) {
      setError(t('promo_code_required'));
      setIsSuccess(false);
      return;
    }

    try {
      const result = await applyPromoCodeMutation({
        sessionId,
        payload: { code },
      }).unwrap();

      const appliedDiscount = normalizeApplyPromoCodeResponse(result);

      if (appliedDiscount?.promoCode) {
        setAppliedCode({
          code: appliedDiscount.promoCode,
          discountType: appliedDiscount.discountType,
          discountValue: appliedDiscount.discountValue,
        });
        setIsSuccess(true);
        setError(null);

        posthog.capture('promo_code_applied', {
          sessionId,
          promoCode: appliedDiscount.promoCode,
          success: true,
          discountType: appliedDiscount.discountType,
          discountValue: appliedDiscount.discountValue,
        });
      } else if (result.failureReason) {
        setError(result.message || getErrorMessage(result.failureReason, code));
        setIsSuccess(false);

        posthog.capture('promo_code_applied', {
          sessionId,
          promoCode: code,
          success: false,
          errorType: result.failureReason,
        });
      }
    } catch (err) {
      setError(`${code} ${t('promo_code_cannot_be_used')}`);
      setIsSuccess(false);

      posthog.capture('promo_code_applied', {
        sessionId,
        promoCode: code,
        success: false,
        errorType: PROMO_CODE_ERRORS.API_ERROR,
      });
    }
  };

  if (appliedCode && !isModalOpen) {
    return (
      <Box className={classes.appliedPromoWrapper}>
        <Box className={classes.appliedPromoFrame} onClick={handleOpen}>
          <img src={CheckCircleIcon} alt='' style={ICON_SIZES.MEDIUM} />
          <Typography className={classes.appliedPromoText}>
            {appliedCode.code} {t('promo_code_applied')}
          </Typography>
          <ChevronRightIcon className={classes.chevronIcon} />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.promoTriggerWrapper}>
        <Box className={classes.promoTriggerFrame} onClick={handleOpen}>
          <Box className={classes.promoTriggerText}>
            <img src={PercentageDiscountIcon} alt='' style={ICON_SIZES.SMALL} />
            <Typography className={classes.promoLabel}>{t('promo_code_add_now')}</Typography>
          </Box>
          <ChevronRightIcon className={classes.chevronIcon} />
        </Box>
      </Box>

      <Modal open={isModalOpen} className={classes.modalOverlay} disableScrollLock>
        <Slide direction='up' in={isModalOpen} timeout={300} mountOnEnter unmountOnExit>
          <Box className={classes.modalContent} tabIndex={-1}>
            <Box className={classes.modalHeader}>
              <Typography className={classes.modalTitle}>{t('promo_code_title')}</Typography>
              <IconButton onClick={handleClose} className={classes.closeButton}>
                <CloseIcon />
              </IconButton>
            </Box>

            {isSuccess && appliedCode ? (
              <Box className={classes.successBox}>
                <img src={CheckCircleIcon} alt='' style={ICON_SIZES.MEDIUM} />
                <Box className={classes.successContent}>
                  <Typography className={classes.successTitle}>
                    {appliedCode.code} {t('promo_code_applied')}
                  </Typography>
                  <Typography className={classes.successDiscount}>
                    {formatDiscountValue(appliedCode.discountType, appliedCode.discountValue, t)}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Typography className={classes.inputLabel}>
                  {t('promo_code_input_label')}
                </Typography>
                <TextField
                  fullWidth
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder={t('promo_code_placeholder')}
                  className={classes.textField}
                  error={!!error}
                />
                {error && <Typography className={classes.errorText}>{error}</Typography>}

                <CustomButton
                  variant='contained'
                  onClick={handleApply}
                  className={classes.applyButton}
                  style={{ backgroundColor: ColorType.DENIM }}
                >
                  {t('promo_code_apply')}
                </CustomButton>
              </>
            )}
          </Box>
        </Slide>
      </Modal>
    </>
  );
};

export default PromoCode;
